import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AuditLogEntry } from '@/api/audit/auditApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { ActivityEntryRow } from './ActivityEntryRow';

interface ActivityLogResultsProps {
  entriesQuery: UseQueryResult<PaginatedResult<AuditLogEntry>, Error>;
  hasActiveFilters: boolean;
  showWorkspace: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function ActivityLogResults({
  entriesQuery,
  hasActiveFilters,
  showWorkspace,
  page,
  pageSize,
  onPageChange,
}: ActivityLogResultsProps) {
  const { t } = useTranslation('activity');
  const result = entriesQuery.data;

  if (entriesQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (entriesQuery.isError === true || result === undefined) {
    return (
      <QueryErrorState
        onRetry={function retryActivity() {
          entriesQuery.refetch();
        }}
      />
    );
  }

  if (result.items.length === 0) {
    return (
      <Card padding="lg" shadow={false} className="text-center text-sm text-zoomuted">
        {hasActiveFilters === true ? t('noResults') : t('empty')}
      </Card>
    );
  }

  return (
    <>
      <Card padding="none" className="overflow-hidden">
        <ul className="divide-y divide-zooborder">
          {result.items.map(function renderEntry(entry) {
            return <ActivityEntryRow key={entry.id} entry={entry} showWorkspace={showWorkspace} />;
          })}
        </ul>
      </Card>
      <ListPagination
        page={page}
        pageSize={pageSize}
        totalItems={result.totalItems}
        onPageChange={onPageChange}
      />
    </>
  );
}
